import { Box, Stack, Typography, useTheme } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusQuantity } from "@smartrr/shared/customerPortal/components/AquariusQuantity";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchasableVariant } from "@smartrr/shared/entities/PurchasableVariant";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { SellingPlanConfigs } from "@smartrr/shared/entities/SellingPlanGroup";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { isDefaultVariant } from "@smartrr/shared/utils/isDefaultVariant";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import _ from "lodash";
import React, { MutableRefObject, Ref, useCallback, useEffect, useMemo, useState } from "react";

import { formatGetPrice } from "@customer-app/account/views/OperationAquarius/utils/Pricing";
import { getItemPriceWithSellingPlan } from "@customer-app/account/views/OperationAquarius/utils/Pricing/Item";
import {
  VariantPurchaseOptions,
  useVariantEnablementForSellingPlanId,
} from "@customer-app/state/reducers/sellingPlanGroups";

import { AddonPricing } from "../AddonList/libs/components/AddonPricing";
import { OrderItemImage } from "../constants";
import { LoyaltyStoreAccess } from "../../Loyalty/store";
import { isCPSPrepaid } from "@smartrr/shared/utils/isPrepaid";
import { SellingPlanPricingPolicyAdjustmentType } from "@smartrr/shared/shopifyGraphQL/api";
import { getPreDiscountPriceWithSingleSellingPlan } from "@customer-app/account/views/OperationAquarius/utils/Pricing/Delivery";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

export interface IItemProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  product: IPurchasable;
  setActivePurchaseable: React.Dispatch<React.SetStateAction<IPurchasable | any>>;
  setSelectedVariantOption?: React.Dispatch<React.SetStateAction<VariantPurchaseOptions | undefined>>;
  setItem?: React.Dispatch<React.SetStateAction<IPurchaseStateLineItem | any>>;
  activeQty: Ref<number | null>;
  item?: IPurchaseStateLineItem;
  activeItem?: IPurchaseStateLineItem | null;
  activePurchaseable: IPurchasable | null;

  /** Callback to be fired when item's quantity < 0 */
  onRemove?: (lineItemId: string) => Promise<void> | null;

  /** Toggle to make item selectable on click opposed to 'Add' Button */
  selectOnClick?: boolean;

  /** Toggle for disabling products - use when loading, or when order is skipped */
  disabled?: boolean;

  /** Toggle to show product variant */
  showVariant?: boolean;

  /** Toggle to show product name */
  showProductName?: boolean;

  /** Toggle to show / hide qtySelector */
  showQtySelector?: boolean;

  /** Toggle to show / hide item's price */
  showPrice?: boolean;

  /** Quantity of item */
  currentQty?: number;

  /** If parent component is loading */
  isLoading?: boolean;

  /** Taken from variant selector dropdown, will change displayed values based on selected variant */
  variantFromDropdown?: IPurchasableVariant | null;

  /** Self-explanatory */
  isPrepaid?: boolean;

  /** Number passed to qty selector */
  maxQty?: number;

  /** Used to render addons prices with subscription discounts */
  renderItemsForSubscription?: boolean;

  configType?: SellingPlanConfigs;

  lazyLoad?: boolean;

  /** If `true` and item is disabled, 'Unavailable' is displayed below variant name */
  showUnavailableTag?: boolean;
}

export const Item = ({
  delivery,
  product,
  setActivePurchaseable,
  setSelectedVariantOption,
  activePurchaseable,
  activeQty,
  selectOnClick = false,
  disabled = false,
  item,
  activeItem,
  setItem,
  showProductName = true,
  showVariant,
  showQtySelector = true,
  showPrice = true,
  currentQty,
  isLoading = false,
  onRemove,
  variantFromDropdown,
  isPrepaid,
  maxQty,
  configType = SellingPlanConfigs.SUBSCRIPTION,
  lazyLoad = false,
  showUnavailableTag = false,
}: IItemProps): JSX.Element => {
  const [showQuantitySelector, setShowQuantitySelector] = useState(false);
  const [quantity, setQuantity] = useState<number>(1);
  const { purchaseState } = delivery;
  const { sellingPlanId } = purchaseState;
  const theme = useTheme();
  const enabledVariantOptions = useVariantEnablementForSellingPlanId(product!, sellingPlanId, true, true);
  const filteredAddonVariants = enabledVariantOptions.filter(option =>
    configType ? !!option[configType] : !!option.addonConfig
  );
  const isActivePurchasable =
    activePurchaseable?.id === product.id &&
    item?.vnt.id === activeItem?.vnt.id &&
    item?.sellingPlanId === activeItem?.sellingPlanId;

  const RemoveHandler = useCallback(() => onRemove && item && onRemove(item.id), []);

  useEffect(() => {
    if (quantity < 1 && !RemoveHandler) {
      setShowQuantitySelector(false); // Changing selector back to button and resetting value
      setActivePurchaseable(null);
      setItem && setItem(null);
      setQuantity(1);
    }

    // This is needed so TS knows that this is a Mutable ref object instead of a ref callback
    (activeQty as MutableRefObject<number | null>).current = quantity;
  }, [quantity]);

  useEffect(() => {
    setQuantity(currentQty || 1);
  }, []);

  const escFunction = useCallback(event => {
    if (event.key === "Escape") {
      setShowQuantitySelector(false);
      setActivePurchaseable(null);
      setItem && setItem(null);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);

    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, []);

  const showAddonVariantName = useMemo(
    () =>
      theme.modernThemeSettings?.subscriptionActions.showVariantName &&
      theme.modernThemeSettings.showHide.showVariantName &&
      filteredAddonVariants &&
      filteredAddonVariants.length === 1 &&
      !isDefaultVariant(filteredAddonVariants[0].vnt.purchasableVariantName),
    []
  );

  const showItemVariantName = useMemo(
    () =>
      item || variantFromDropdown
        ? !isDefaultVariant(item?.vnt.purchasableVariantName) &&
          !isDefaultVariant(variantFromDropdown?.purchasableVariantName)
        : false,
    []
  );

  useEffect(() => {
    product && !isActivePurchasable && showQuantitySelector && setShowQuantitySelector(false);
    setItem && activeItem?.id !== item?.id && showQuantitySelector && setShowQuantitySelector(false);
  }, [activeItem?.id, activePurchaseable, item?.id, setItem, showQuantitySelector]);

  const setActive = useCallback(() => {
    setSelectedVariantOption && setSelectedVariantOption(undefined);
    setShowQuantitySelector(true);
    setActivePurchaseable(product);
    setItem && setItem(item);
    (activeQty as MutableRefObject<number | null>).current = quantity;
  }, [item, product, setActivePurchaseable, setItem, activeQty, quantity]);
  const isActive = activeItem?.id === item?.id;
  const variantName =
    isActive && variantFromDropdown
      ? variantFromDropdown?.purchasableVariantName
      : item?.vnt.purchasableVariantName;
  const variant = isActive && variantFromDropdown ? variantFromDropdown : item?.vnt;
  return (
    <Box
      onClick={selectOnClick && product && !isActivePurchasable && !disabled ? setActive : () => null}
      sx={{
        position: "relative",
        ...(selectOnClick &&
          !disabled && {
            "&:hover": {
              cursor: "pointer",
            },
          }),
        ...(disabled && {
          opacity: ".6",
        }),
      }}
    >
      <OrderItemImage
        purchasable={product}
        purchasableVariant={variant}
        quantity={item?.quantity || undefined}
        size="medium"
        lazyLoading={lazyLoad}
      />
      {showQuantitySelector && showQtySelector ? (
        <React.Fragment>
          <AquariusQuantity
            className={prefixClassName("edit-item-quantity-container")}
            value={quantity}
            isLoading={isLoading}
            onChange={setQuantity}
            maxQty={maxQty}
          />
        </React.Fragment>
      ) : !selectOnClick && !disabled && !isPrepaid && !item?.isRedeemed ? (
        <AquariusButton onClick={setActive}>{typedLocalize("general.add")}</AquariusButton>
      ) : null}
      {showProductName && product ? (
        <Typography
          variant="body1"
          className={prefixClassName("product-name")}
          style={{ marginTop: selectOnClick || item?.isRedeemed ? "7px" : "0px" }}
        >
          {_.truncate(product.purchasableName)}
        </Typography>
      ) : null}

      {showVariant && item ? (
        <React.Fragment>
          {showItemVariantName ? (
            <Box display="flex" sx={{ marginBottom: "3px" }}>
              <Typography variant="caption1" className={prefixClassName("product-variant-name")}>
                {_.truncate(variantName)}
              </Typography>
            </Box>
          ) : null}
          {/* Pricing information for only ITEMS */}
          {showPrice ? (
            <ItemPricing
              variantFromDropdown={variantFromDropdown}
              item={item}
              delivery={delivery}
              enabledVariantOptions={enabledVariantOptions}
            />
          ) : (
            <React.Fragment />
          )}

          {showUnavailableTag && disabled ? (
            <Box display="flex" sx={{ marginBottom: "3px" }}>
              <Typography variant="caption1" className={prefixClassName("product-variant-name")}>
                {typedLocalize("general.unavailable")}
              </Typography>
            </Box>
          ) : (
            <React.Fragment />
          )}

          {onRemove ? (
            <Box display="flex">
              <Typography
                variant="caption1"
                onClick={RemoveHandler}
                sx={{
                  "&:hover": {
                    cursor: "pointer",
                    textDecoration: "underline",
                  },
                }}
              >
                {typedLocalize("general.remove")}
              </Typography>
            </Box>
          ) : null}
        </React.Fragment>
      ) : (
        <Box display="flex" flexDirection="column">
          {showAddonVariantName ? (
            <Box display="flex" sx={{ marginBottom: "3px" }}>
              <Typography variant="caption1" className={prefixClassName("product-variant-name")}>
                {_.truncate(filteredAddonVariants[0].vnt.purchasableVariantName)}
              </Typography>
            </Box>
          ) : null}

          {showPrice ? (
            <AddonPricing
              currency={purchaseState.currency}
              variantFromDropdown={variantFromDropdown}
              enabledVariantOptions={enabledVariantOptions}
              filteredAddonVariants={filteredAddonVariants}
              configType={configType}
              delivery={delivery}
            />
          ) : (
            <React.Fragment />
          )}
        </Box>
      )}
    </Box>
  );
};

interface ItemPricingProps {
  variantFromDropdown?: IPurchasableVariant | null | undefined;
  item: IPurchaseStateLineItem;
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  enabledVariantOptions: VariantPurchaseOptions[];
}

export const ItemPricing = ({
  enabledVariantOptions,
  variantFromDropdown,
  item,
  delivery,
}: ItemPricingProps): JSX.Element => {
  const customerCountryCode = useSmartrrCustomerSelector(
    state => state.auth.customerRelationship?.defaultAddress?.countryCode
  );
  const variant: VariantPurchaseOptions | undefined = variantFromDropdown
    ? (enabledVariantOptions.find(item => item.vnt.id === variantFromDropdown.id) ?? enabledVariantOptions[0])
    : (enabledVariantOptions.find(itemToCheck => item.vnt.id === itemToCheck.vnt.id) ?? enabledVariantOptions[0]);

  /**
   * Condition for rendering `Free` in place of item price when line item is apart of special referrals program
   * This relates to Jolie's special referral requests
   */
  const isDiscountedSpecialReferralsVariant = LoyaltyStoreAccess.useIsDiscountReferralsVariant(
    item,
    delivery.indexFromNext,
    delivery.purchaseState.subProperties
  );
  /*
   ** Work around to stop Manage Order / Manage Subscription modal from crashing when opening
   ** subscriptions with bundles or products that have been removed from subscription group
   */
  if (
    !variant ||
    enabledVariantOptions.length === 0 ||
    (item.isRedeemed && !isDiscountedSpecialReferralsVariant)
  ) {
    return <React.Fragment />;
  }

  const getRelevantSellingPlan = (item: IPurchaseStateLineItem, options: VariantPurchaseOptions) => {
    if (!options) {
      return null;
    }
    const sellingPlans = [options.sellingPlan, options.addonConfig, options.trendingConfig];
    const sellingPlan = sellingPlans.find(sellingPlan => {
      return !!item.sellingPlanId && sellingPlan?.shopifyNumericId === shopifyGidToNumber(item.sellingPlanId);
    });
    return sellingPlan ?? options.sellingPlan;
  };

  const relevantSellingPlan = getRelevantSellingPlan(item, variant);

  const isVariantSameAsLineItem = variant.vnt.id === item.vnt.id;

  const price = getItemPriceWithSellingPlan(
    variant.vnt,
    item.priceCurrency,
    relevantSellingPlan,
    (delivery.purchaseState.schedule.totalOrdersCount ?? 1) + delivery.indexFromScheduleStart,
    isVariantSameAsLineItem ? item.pricingPolicy : null,
    customerCountryCode
  );

  const isPrepaid = isCPSPrepaid(delivery.purchaseState.schedule);
  const isSequential = !!item.sequential;

  if (
    isPrepaid &&
    item.pricingPolicy?.cycleDiscounts[0].adjustmentType === SellingPlanPricingPolicyAdjustmentType.Price
  ) {
    price.current.price = price.current.price / delivery.purchaseState.schedule.paymentFrequencyMultiple;
  }

  if (isSequential) {
    const cps = delivery.purchaseState;
    const { amount } = getPreDiscountPriceWithSingleSellingPlan(
      delivery.purchaseState.sellingPlanId ?? null,
      [item],
      relevantSellingPlan!,
      (cps.schedule.totalOrdersCount ?? 1) + delivery.indexFromScheduleStart,
      cps.currency,
      delivery.paymentMultipleDueOnDate,
      cps.schedule.paymentFrequencyMultiple > 1
    );

    price.current.price = amount.current.price;

    if (isPrepaid) {
      price.current.price = price.current.price / delivery.purchaseState.schedule.paymentFrequencyMultiple;
    }
  }

  const newPrice = formatGetPrice(price, item.priceCurrency);

  const hasDiscount = newPrice.current !== newPrice.initial;

  return (
    <Stack
      direction="row"
      sx={{ marginBottom: "3px", flexWrap: "wrap" }}
      className={prefixClassName("item-pricing-container")}
    >
      {isDiscountedSpecialReferralsVariant ? (
        <Typography
          variant="caption1"
          className={prefixClassName("product-after-discount-price")}
          data-testid="after-discount-price"
          sx={{ marginRight: "3px" }}
        >
          {typedLocalize("general.free")}
        </Typography>
      ) : (
        <React.Fragment>
          <Typography
            variant="caption1"
            className={prefixClassName("product-after-discount-price")}
            data-testid="after-discount-price"
            sx={{ marginRight: "3px" }}
          >
            {newPrice.current}
          </Typography>
          {!!hasDiscount && (
            <Typography
              variant="caption1"
              style={{ textDecoration: "line-through", opacity: "70%" }}
              className={prefixClassName("product-before-discount-price")}
              data-testid="before-discount-price"
            >
              {newPrice.initial}
            </Typography>
          )}
        </React.Fragment>
      )}
    </Stack>
  );
};
